/**
 * MchriApi: authenticated fetch helpers and the shared chart data manager.
 *
 * Loaded on the dashboard and report builder (enqueued as
 * "mchri-chart-data-manager"). Expects `window.mchriApi = { restUrl, nonce }`
 * from wp_localize_script.
 *
 * - Every request carries the REST nonce and same-origin cookies, because
 *   the chart endpoints require a logged-in user.
 * - The data manager de-duplicates concurrent requests per date range and
 *   caches the last few ranges, so a filter change during an in-flight load
 *   can no longer be answered with the previous range's data.
 */
(function (root) {
  "use strict";

  var MAX_CACHED_RANGES = 8;

  function apiConfig() {
    return root.mchriApi || {};
  }

  function apiError(message, code, status) {
    var err = new Error(message);
    err.code = code;
    err.status = status;
    return err;
  }

  /**
   * GET a JSON endpoint with the REST nonce. Rejects with an Error carrying
   * `code` ("session_expired", "not_logged_in", "http_error") and `status`.
   */
  async function fetchJson(url, options) {
    options = options || {};
    var doFetch = options.fetch || root.fetch.bind(root);
    var headers = { Accept: "application/json" };
    var nonce = apiConfig().nonce;
    if (nonce) {
      headers["X-WP-Nonce"] = nonce;
    }

    var response = await doFetch(url, { method: "GET", headers: headers, credentials: "same-origin" });
    if (response.ok) {
      return response.json();
    }

    var body = {};
    try {
      body = await response.json();
    } catch (e) {
      body = {};
    }

    if (response.status === 403 && body.code === "rest_cookie_invalid_nonce") {
      throw apiError("Your session has expired. Please reload the page.", "session_expired", 403);
    }
    if (response.status === 401) {
      throw apiError("Please log in to view this data.", "not_logged_in", 401);
    }
    throw apiError(body.message || "HTTP error! status: " + response.status, body.code || "http_error", response.status);
  }

  function batchUrl(startDate, endDate) {
    var base = apiConfig().batchUrl || apiConfig().restUrl + "mchri/v1/form-submissions/tracked-fields-batch";
    var params = new URLSearchParams();
    if (startDate) {
      params.append("start_date", startDate);
    }
    if (endDate) {
      params.append("end_date", endDate);
    }
    var qs = params.toString();
    return qs ? base + (base.indexOf("?") === -1 ? "?" : "&") + qs : base;
  }

  /**
   * Create a chart data manager. `options.fetch` overrides the fetch
   * implementation (used by tests).
   */
  function createChartDataManager(options) {
    options = options || {};
    var cache = new Map(); // cacheKey -> data
    var inflight = new Map(); // cacheKey -> promise
    var lastKey = null;
    var subscribers = [];

    function keyFor(startDate, endDate) {
      return (startDate || "default") + "-" + (endDate || "default");
    }

    function remember(key, data) {
      cache.delete(key);
      cache.set(key, data);
      while (cache.size > MAX_CACHED_RANGES) {
        cache.delete(cache.keys().next().value);
      }
      lastKey = key;
    }

    function fetchBatchData(startDate, endDate) {
      var key = keyFor(startDate, endDate);
      if (cache.has(key)) {
        lastKey = key;
        return Promise.resolve(cache.get(key));
      }
      if (inflight.has(key)) {
        return inflight.get(key);
      }

      var promise = fetchJson(batchUrl(startDate, endDate), { fetch: options.fetch })
        .then(function (data) {
          remember(key, data);
          notifySubscribers(data);
          return data;
        })
        .finally(function () {
          inflight.delete(key);
        });

      inflight.set(key, promise);
      return promise;
    }

    function getFieldData(fieldName) {
      var data = lastKey !== null ? cache.get(lastKey) : null;
      if (!data || !data.fields) {
        return null;
      }
      return data.fields[fieldName] || null;
    }

    function subscribe(callback) {
      subscribers.push(callback);
      if (lastKey !== null && cache.has(lastKey)) {
        callback(cache.get(lastKey));
      }
    }

    function notifySubscribers(data) {
      subscribers.forEach(function (callback) {
        try {
          callback(data);
        } catch (error) {
          console.error("[ChartDataManager] Error in subscriber callback:", error);
        }
      });
    }

    function clearCache() {
      cache.clear();
      inflight.clear();
      lastKey = null;
    }

    function isLoaded() {
      return lastKey !== null && cache.has(lastKey);
    }

    return {
      fetchBatchData: fetchBatchData,
      getFieldData: getFieldData,
      subscribe: subscribe,
      clearCache: clearCache,
      isLoaded: isLoaded,
    };
  }

  root.MchriApi = root.MchriApi || {
    fetchJson: fetchJson,
    batchUrl: batchUrl,
    createChartDataManager: createChartDataManager,
  };

  // Shared instance used by the chart templates.
  root.ChartDataManager = root.ChartDataManager || createChartDataManager();
})(typeof window !== "undefined" ? window : globalThis);
